import React, { useEffect, useState } from 'react';
import { Wallet, ArrowDownCircle, ArrowUpCircle, Clock, CheckCircle, XCircle, Gift } from 'lucide-react';
import walletService from '../../services/walletService';

const statusIcon = (status) => {
  if (status === 'success') return <CheckCircle className="w-4 h-4 text-green-600" />;
  if (status === 'failed')  return <XCircle className="w-4 h-4 text-red-500" />;
  return <Clock className="w-4 h-4 text-yellow-500" />;
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function WalletSection() {
  const [wallet, setWallet]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadWallet(); }, []);

  const loadWallet = async () => {
    setLoading(true);
    const result = await walletService.getWallet();
    if (result.success) setWallet(result.data);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3" />
          <div className="h-24 bg-gray-100 rounded-xl" />
          <div className="h-12 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  const balance = wallet?.balance ?? 0;
  const transactions = wallet?.transactions ?? [];

  return (
    <div className="space-y-6">
      {/* Balance Card */}
      <div className="bg-gradient-to-br from-[#2e4e18] to-[#548B2E] rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Wallet className="w-5 h-5" />
          </div>
          <div>
            <p className="text-white/70 text-sm">BMF Coins</p>
            <p className="font-semibold">Available Balance</p>
          </div>
        </div>
        <p className="text-4xl font-bold mb-1">₹{Number(balance).toFixed(2)}</p>
        <p className="text-white/60 text-sm">Use at checkout to pay for orders</p>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Transaction History</h3>
        {transactions.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <Wallet className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No transactions yet</p>
            <p className="text-sm">BMF Coins will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((txn) => {
              const isAdminCredit = txn.type === 'admin_credit';
              const isCredit = txn.type === 'credit' || isAdminCredit;
              const refId = txn.transactionRefId || txn.transaction_ref_id;
              const hasBankTxnId = Boolean(refId && String(refId).trim());
              const isBankTransfer = (txn.category === null || txn.category === undefined || String(txn.category).toUpperCase() === 'BANK_TRANSFER') && hasBankTxnId;

              let iconBg = 'bg-red-100';
              let iconEl = <ArrowUpCircle className="w-5 h-5 text-red-500" />;
              let amountColor = 'text-red-500';
              let defaultDesc = 'Order Payment';
              if (isAdminCredit) {
                iconBg = 'bg-amber-100';
                iconEl = <Gift className="w-5 h-5 text-amber-600" />;
                amountColor = 'text-amber-600';
                defaultDesc = 'Admin Points Credit';
              } else if (isCredit) {
                iconBg = 'bg-green-100';
                iconEl = <ArrowDownCircle className="w-5 h-5 text-green-600" />;
                amountColor = 'text-green-600';
                defaultDesc = 'BMF Coins Added';
              } else if (isBankTransfer) {
                defaultDesc = 'Bank Transfer Payout';
              }

              const displayTitle = txn.description?.trim() || defaultDesc;

              return (
                <div key={txn.id} className="p-4 bg-gray-50 rounded-xl space-y-2">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                      {iconEl}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-800 text-sm truncate">
                          {displayTitle}
                        </p>
                        {isAdminCredit && (
                          <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                            POINTS
                          </span>
                        )}
                        {isBankTransfer && (
                          <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">
                            BANK TRANSFER
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{formatDate(txn.createdAt)}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`font-bold ${amountColor}`}>
                        {isCredit ? '+' : '−'}₹{Number(txn.amount).toFixed(2)}
                      </p>
                      <div className="flex items-center justify-end gap-1 mt-0.5">
                        {statusIcon(txn.status)}
                        <span className="text-xs text-gray-400 capitalize">{txn.status}</span>
                      </div>
                    </div>
                  </div>

                  {/* Extra Details: Bank Txn ID & Description */}
                  {(hasBankTxnId || (txn.description && txn.description.trim() !== displayTitle)) && (
                    <div className="pt-2 border-t border-gray-200/60 text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
                      {hasBankTxnId && (
                        <div>
                          <span className="text-gray-400 font-medium">Bank Txn ID: </span>
                          <span className="font-mono font-semibold text-gray-700">{refId.trim()}</span>
                        </div>
                      )}
                      {txn.description && txn.description.trim() !== displayTitle && (
                        <div>
                          <span className="text-gray-400 font-medium">Note: </span>
                          <span className="text-gray-700">{txn.description.trim()}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
